/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExpandableCard } from 'src/core/components/ExpandableCard';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { FeatureToggles } from 'src/core/utils/featureToggles';

type AutoTaggingQuotaConsumptionCardProps = {
    totalConsumedQuota?: number;
    totalMonthlyQuota?: number;
    selectedTextRecords?: number;
};

const messages = defineMessages({
    cardName: { id: 'customText.autoTaggingQuotaConsumptionCard.cardName', defaultMessage: 'Auto-labeling quota consumption card.' },
    title: {
        id: 'customText.autoTaggingQuotaConsumptionCard.title',
        defaultMessage: `You can use your model to auto-label up to {remainingQuota} more text records this month.`
    },
    monthlyQuotaMessage: {
        id: 'customText.autoTaggingQuotaConsumptionCard.monthlyQuotaMessage',
        defaultMessage: `The auto-labeling based on your model is limited to {monthlyQuota} text records per resource and resets at
        the start of each month. Each document counts as at least 1 text record for the first 1000 characters plus another text
        record for each additional 1000 characters. For example, a document that is 2200 characters long would count as 3 text records.`
    },
    consumedQuotaMessage: {
        id: 'customText.autoTaggingQuotaConsumptionCard.consumedQuotaMessage',
        defaultMessage: `Number of text records consumed this month`
    },
    consumedQuotaDetailsMessage: {
        id: 'customText.autoTaggingQuotaConsumptionCard.consumedQuotaDetailsMessage',
        defaultMessage: `{consumedQuota} of {monthlyQuota}`
    },
    updatingNumbersSectionMessage: {
        id: 'customText.autoTaggingQuotaConsumptionCard.updatingNumbersSectionMessage',
        defaultMessage: `Text records used so far this month`
    },
    outOfQuota: {
        id: 'customText.autoTaggingQuotaConsumptionCard.outOfQuota',
        defaultMessage: `You have exceeded your quota by {remainingChars} text records`
    },
    almostOutOfQuota: {
        id: 'customText.autoTaggingQuotaConsumptionCard.almostOutOfQuota',
        defaultMessage: `You are about to hit your quota`
    },
    noQuotaLimitForGpt: {
        id: 'customText.autoTaggingQuotaConsumptionCard.noQuotaLimitForGpt',
        defaultMessage: 'There is no monthly limit on auto-labeling with Generative Pre Trained Transformer (GPT).'
    },
    learnMore: {
        id: 'customText.autoTaggingQuotaConsumptionCard.learnMore',
        defaultMessage: 'Learn more about text record usage limits with auto-labeling.'
    }
});

export const AutoTaggingQuotaConsumptionCard = observer((props: AutoTaggingQuotaConsumptionCardProps) => {
    const { totalConsumedQuota, selectedTextRecords = null, totalMonthlyQuota } = props;
    const { isXLargeOrGreater } = useResponsiveMode();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { localize } = useLocalization();

    const stringifiedSelectedChar = selectedTextRecords?.toLocaleString() ?? 'XX';
    const stringifiedConsumedQuota = totalConsumedQuota?.toLocaleString() ?? 'XX';
    const stringifiedQuotaLimits = totalMonthlyQuota?.toLocaleString() ?? 'XXXX';
    const totalRemainingQuota = totalMonthlyQuota ? totalMonthlyQuota - totalConsumedQuota : totalConsumedQuota;
    const stringifiedRemainingQuota = totalRemainingQuota?.toLocaleString() ?? 'XXXX';
    const selectionRemainingQuota = totalRemainingQuota - selectedTextRecords;
    const expandingIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
        root: { fontSize: 12, transform: `rotate(${isExpanded ? '0' : '180deg'})`, transition: 'transform 300ms ease-in-out' }
    });

    let messageBarType;
    const getMessageBarText = () => {
        if (!totalConsumedQuota && !totalMonthlyQuota) {
            messageBarType = MessageBarType.info;

            return (
                <LuisText>
                    {localize(messages.title, { remainingQuota: stringifiedRemainingQuota })}
                    {FeatureToggles.CustomTextGptLabeling && (
                        <LuisText fontWeight={600}>&nbsp;{localize(messages.noQuotaLimitForGpt)}</LuisText>
                    )}
                </LuisText>
            );
        }
        if (totalConsumedQuota >= totalMonthlyQuota) {
            messageBarType = MessageBarType.error;

            return localize(messages.outOfQuota, { remainingChars: Math.abs(totalMonthlyQuota - totalConsumedQuota).toLocaleString() });
        }
        if (totalConsumedQuota < 0 || selectionRemainingQuota < 0) {
            messageBarType = MessageBarType.error;

            return localize(messages.outOfQuota, { remainingChars: Math.abs(selectionRemainingQuota).toLocaleString() });
        }
        if (totalConsumedQuota / totalMonthlyQuota >= 0.8 || selectedTextRecords / totalRemainingQuota >= 0.8) {
            messageBarType = MessageBarType.warning;

            return localize(messages.almostOutOfQuota);
        }

        return (
            <LuisText>
                {localize(messages.title, { remainingQuota: stringifiedRemainingQuota })}
                {FeatureToggles.CustomTextGptLabeling && (
                    <LuisText fontWeight={600}>&nbsp;{localize(messages.noQuotaLimitForGpt)}</LuisText>
                )}
            </LuisText>
        );
    };

    const messageBarText = getMessageBarText();
    const CardHeader = (
        <MessageBar messageBarType={messageBarType} styles={{ innerText: { width: '100%' } }}>
            <Stack horizontal horizontalAlign="space-between">
                {messageBarText}
                <Icon iconName="ChevronUp" styles={expandingIconStyles} />
            </Stack>
        </MessageBar>
    );

    return (
        <ExpandableCard
            isChevronHidden
            isExpanded={isExpanded}
            onRenderHeader={CardHeader}
            isSeparatorDisplayed={false}
            onExpansionChange={setIsExpanded}
            cardName={localize(messages.cardName)}
            styles={{ headerContainer: { padding: 0 }, root: { flex: 0 } }}>
            <Stack
                horizontal={isXLargeOrGreater}
                tokens={{ childrenGap: isXLargeOrGreater ? 33 : 16, padding: !isXLargeOrGreater && '0 24px 0 0' }}
                styles={{ root: { margin: '0 0 10px 24px' } }}>
                {selectedTextRecords !== null && (
                    <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                        <ProgressIndicator
                            label={localize(messages.updatingNumbersSectionMessage)}
                            description={localize(messages.consumedQuotaDetailsMessage, {
                                consumedQuota: stringifiedSelectedChar,
                                monthlyQuota: stringifiedRemainingQuota
                            })}
                            percentComplete={selectedTextRecords / totalRemainingQuota}
                        />
                    </Stack>
                )}
                <Stack.Item>
                    <ProgressIndicator
                        label={localize(messages.consumedQuotaMessage)}
                        description={localize(messages.consumedQuotaDetailsMessage, {
                            consumedQuota: stringifiedConsumedQuota,
                            monthlyQuota: stringifiedQuotaLimits
                        })}
                        percentComplete={totalConsumedQuota / totalMonthlyQuota}
                    />
                </Stack.Item>
                {selectedTextRecords === null && (
                    <Stack.Item styles={{ root: { paddingTop: '4px' } }}>
                        <LuisText>
                            {localize(messages.monthlyQuotaMessage, { monthlyQuota: stringifiedQuotaLimits })}&nbsp;
                            <ExternalLink>{localize(messages.learnMore)}</ExternalLink>
                        </LuisText>
                    </Stack.Item>
                )}
            </Stack>
        </ExpandableCard>
    );
});
